import {
  Sparkles,
  Wand2,
  GitCompare,
  Clock,
  FileText,
  BookOpen,
  Grid3x3,
  ChevronRight,
  Database,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { HubShell } from '@/components/layout/HubShell';
import { AppsPage } from './AppsPage';
import { EmbeddedSkillRail } from '@/components/skill';
import { AppGenerator } from './AppGenerator';

/**
 * Apps hub — every app-generation and developer surface.
 *
 * The hub itself contains Apps list, Templates, and Developer tooling.
 * Full-page generators live at sub-routes:
 *   /apps/compare   → ComparisonBuilder
 *   /apps/timeline  → TimelineBuilder
 *   /apps/synthesize→ ResearchSynthesis
 */
export function AppsHubPage() {
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Sparkles,
      render: () => <AppsOverview />,
    },
    {
      id: 'generate',
      label: 'Generate App',
      icon: Wand2,
      render: () => <AppGenerator />,
    },
    {
      id: 'apps',
      label: 'My Apps',
      icon: Grid3x3,
      render: () => <AppsPage />,
    },
  ];

  return (
    <HubShell
      title="Apps"
      description="Generate, browse, and ship apps built from your open tabs."
      icon={Sparkles}
      tabs={tabs}
      defaultSection="overview"
    />
  );
}

function AppsOverview() {
  const generators = [
    {
      title: 'Generate App',
      desc: 'Turn open tabs into a working web app.',
      to: '/apps/generate',
      icon: Wand2,
      color: 'from-violet-500/30 to-fuchsia-500/30',
    },
    {
      title: 'Compare',
      desc: 'Build a side-by-side comparison table.',
      to: '/apps/compare',
      icon: GitCompare,
      color: 'from-cyan-500/30 to-blue-500/30',
    },
    {
      title: 'Timeline',
      desc: 'Build a visual timeline from your tabs.',
      to: '/apps/timeline',
      icon: Clock,
      color: 'from-amber-500/30 to-rose-500/30',
    },
    {
      title: 'Research Synthesis',
      desc: 'Distil research tabs into one summary.',
      to: '/apps/synthesize',
      icon: FileText,
      color: 'from-emerald-500/30 to-teal-500/30',
    },
    {
      title: 'Research Paper',
      desc: 'Generate a journal-style manuscript from your tabs.',
      to: '/apps/synthesize',
      icon: BookOpen,
      color: 'from-sky-500/30 to-indigo-500/30',
    },
    {
      title: 'Data Aggregation',
      desc: 'Combine structured data from multiple tabs into one dataset.',
      to: '/apps/data-aggregation',
      icon: Database,
      color: 'from-orange-500/30 to-amber-500/30',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        {generators.map((g) => {
          const Icon = g.icon;
          return (
            <Link
              key={g.title}
              to={g.to}
              className="group relative flex items-start gap-4 overflow-hidden rounded-2xl border border-khiimori-border bg-khiimori-bg-elevated/40 p-5 backdrop-blur-xl transition-all hover:border-primary-500/40 hover:bg-khiimori-bg-elevated/70"
            >
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${g.color}`}>
                <Icon className="h-6 w-6 text-khiimori-text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-khiimori-text-primary">{g.title}</div>
                <div className="text-xs text-khiimori-text-secondary/60">{g.desc}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-khiimori-text-secondary/40 transition-transform group-hover:translate-x-1" />
            </Link>
          );
        })}
      </div>

      <EmbeddedSkillRail
        title="Generator skills"
        restrictCategories={['generate']}
      />

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-khiimori-text-primary">Recently generated</h3>
          <Link to="/apps" className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300">
            Browse all apps <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <AppsPage />
      </div>
    </div>
  );
}

export default AppsHubPage;
